import { useEffect, useState } from 'react'
import { Archive, ArchiveRestore, Pencil, Trash2, X } from 'lucide-react'
import { useConversations } from '../../contexts/ConversationContext'

interface Props {
  conv: {
    id: string
    title: string | null
    is_archived: boolean
  } | null
  onClose: () => void
}

/**
 * Bottom-sheet modal for conversation actions on mobile. Opens on
 * long-press of a row in MobileConversationsList. Actions wrap the
 * existing ConversationContext handlers — no new backend endpoints.
 *
 * Three actions: rename (inline input inside the sheet), archive/
 * unarchive (toggles `is_archived`), delete (with confirm). Destructive
 * action is visually separated at the bottom with a divider.
 *
 * Pattern: controlled component. Mount always, visibility driven by
 * `conv !== null`. Backdrop click + X button + Escape key all close.
 */
export function ConversationActionsSheet({ conv, onClose }: Props) {
  const { updateConversation, deleteConversation } = useConversations()
  const [mode, setMode] = useState<'menu' | 'rename'>('menu')
  const [renameValue, setRenameValue] = useState('')
  const [busy, setBusy] = useState(false)

  // Reset internal state whenever the target conv changes (or closes).
  useEffect(() => {
    if (conv) {
      setMode('menu')
      setRenameValue(conv.title || '')
    } else {
      setMode('menu')
      setBusy(false)
    }
  }, [conv])

  // Escape closes.
  useEffect(() => {
    if (!conv) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [conv, onClose])

  if (!conv) return null

  async function handleRename() {
    if (!conv) return
    const next = renameValue.trim() || conv.title || 'Nova conversa'
    if (next === conv.title) {
      onClose()
      return
    }
    setBusy(true)
    try {
      await updateConversation(conv.id, { title: next })
      onClose()
    } finally {
      setBusy(false)
    }
  }

  async function handleArchive() {
    if (!conv) return
    setBusy(true)
    try {
      await updateConversation(conv.id, { is_archived: !conv.is_archived })
      onClose()
    } finally {
      setBusy(false)
    }
  }

  async function handleDelete() {
    if (!conv) return
    if (!confirm(`Apagar "${conv.title || 'Nova conversa'}"? Esta acção não pode ser desfeita.`))
      return
    setBusy(true)
    try {
      await deleteConversation(conv.id)
      onClose()
    } finally {
      setBusy(false)
    }
  }

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    width: '100%',
    padding: '16px 20px',
    border: 'none',
    background: 'transparent',
    fontFamily: 'var(--sans)',
    fontSize: '15px',
    color: 'var(--ink)',
    textAlign: 'left',
    cursor: busy ? 'wait' : 'pointer',
    opacity: busy ? 0.5 : 1,
  }

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.4)',
          zIndex: 100,
          animation: 'fadeIn 0.15s ease-out',
        }}
      />

      {/* Sheet */}
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 101,
          background: 'var(--cream)',
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px',
          boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.1)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          animation: 'slideUp 0.18s ease-out',
        }}
      >
        {/* Drag-handle visual + close */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 16px 6px',
          }}
        >
          <div
            style={{
              width: '36px',
              height: '4px',
              borderRadius: '2px',
              background: 'var(--border)',
              margin: '0 auto',
            }}
          />
          <button
            onClick={onClose}
            aria-label="Fechar"
            style={{
              position: 'absolute',
              right: '12px',
              top: '8px',
              padding: '6px',
              border: 'none',
              background: 'transparent',
              color: 'var(--ink-muted)',
              cursor: 'pointer',
            }}
          >
            <X size={18} strokeWidth={1.75} />
          </button>
        </div>

        {/* Title of target conv */}
        <div
          style={{
            padding: '4px 20px 12px',
            fontFamily: 'var(--sans)',
            fontSize: '12px',
            color: 'var(--ink-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            borderBottom: '1px solid var(--border)',
          }}
        >
          {conv.title || 'Nova conversa'}
        </div>

        {mode === 'rename' ? (
          <div style={{ padding: '16px 20px' }}>
            <label
              style={{
                display: 'block',
                fontFamily: 'var(--sans)',
                fontSize: '12px',
                color: 'var(--ink-muted)',
                marginBottom: '6px',
              }}
            >
              Novo título
            </label>
            <input
              autoFocus
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRename()
                if (e.key === 'Escape') setMode('menu')
              }}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                fontFamily: 'var(--sans)',
                fontSize: '15px',
                background: 'white',
                color: 'var(--ink)',
                outline: 'none',
              }}
            />
            <div
              style={{
                display: 'flex',
                gap: '8px',
                marginTop: '12px',
                justifyContent: 'flex-end',
              }}
            >
              <button
                onClick={() => setMode('menu')}
                disabled={busy}
                style={{
                  padding: '8px 14px',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  background: 'transparent',
                  color: 'var(--ink-muted)',
                  fontFamily: 'var(--sans)',
                  fontSize: '13px',
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleRename}
                disabled={busy}
                style={{
                  padding: '8px 14px',
                  border: 'none',
                  borderRadius: '8px',
                  background: 'var(--ink)',
                  color: 'var(--cream)',
                  fontFamily: 'var(--sans)',
                  fontSize: '13px',
                  fontWeight: 500,
                }}
              >
                {busy ? 'A guardar...' : 'Guardar'}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ padding: '4px 0 8px' }}>
            <button onClick={() => setMode('rename')} disabled={busy} style={rowStyle}>
              <Pencil size={17} strokeWidth={1.5} />
              Renomear
            </button>
            <button onClick={handleArchive} disabled={busy} style={rowStyle}>
              {conv.is_archived ? (
                <ArchiveRestore size={17} strokeWidth={1.5} />
              ) : (
                <Archive size={17} strokeWidth={1.5} />
              )}
              {conv.is_archived ? 'Desarquivar' : 'Arquivar'}
            </button>
            <div
              style={{
                borderTop: '1px solid var(--border)',
                margin: '4px 0',
              }}
            />
            <button
              onClick={handleDelete}
              disabled={busy}
              style={{ ...rowStyle, color: '#dc2626' }}
            >
              <Trash2 size={17} strokeWidth={1.5} />
              Apagar
            </button>
          </div>
        )}
      </div>

      {/* Keyframes — scoped locally via inline <style>. */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>
    </>
  )
}
